import { environment } from "@roman/shared/utils/environment";
import { getPlatform, isMobileAgent } from "@roman/shared/utils/platform";
import { useEffect } from "react";

type UseNativeAppRedirectOptions = {
  /** The native app deep link scheme path */
  deepLinkPath: string;
  /** Whether to enable the redirect (default: true) */
  enabled?: boolean;
  /** Timeout in milliseconds before showing web content (default: 1500) */
  timeout?: number;
};

const scheme = environment.web.isProd ? "thumba" : "thumba-stg";
/**
 * Hook to redirect mobile users to native app using deep links
 * If the app is not installed, it will fallback to showing web content
 */

// safariかどうか
const isIos = getPlatform() === "ios";

export const useNativeAppRedirect = (options: UseNativeAppRedirectOptions) => {
  const { deepLinkPath, enabled = true, timeout = 1500 } = options;

  const moveToNativeApp = () => {
    window.location.href = `${scheme}://${deepLinkPath}`;
  };

  useEffect(() => {
    if (!enabled || !isMobileAgent()) {
      return;
    }

    const deepLink = `${scheme}://${deepLinkPath}`;
    let timeoutId: NodeJS.Timeout;

    // Try to open the native app
    const tryOpenNativeApp = () => {
      // Create a hidden iframe to trigger the deep link
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = deepLink;
      document.body.appendChild(iframe);

      // Fallback: if app is not installed, the page will remain visible
      timeoutId = setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, timeout);

      // Alternative method for iOS: try using window.location with error handling (except safari)
      if (!isIos) {
        setTimeout(() => {
          try {
            window.location.href = deepLink;
          } catch (error) {
            // Silently fail if the deep link is invalid or app is not installed
            console.debug("Deep link redirect failed:", error);
          }
        }, 25);
      }
    };

    // Execute redirect after a short delay to ensure page is loaded
    const initTimeout = setTimeout(() => {
      tryOpenNativeApp();
    }, 100);

    return () => {
      clearTimeout(initTimeout);
      clearTimeout(timeoutId);
    };
  }, [deepLinkPath, enabled, timeout]);

  return { moveToNativeApp };
};
